import { useContext, useState, useEffect, useRef } from "react";
import { AlarmContext } from "../context/AlarmContext";
import "./TopBar.css";
import UserInformation from "./UserInformation";
import ProductionSummary from "./ProductionSummary";
import FAQ from "./FAQ";
import defaultAvatar from "../assets/avatar.jpg";
import { clearLoginTime } from "../utils/sessionTime";

const handleLogout = () => {
  clearLoginTime();
  // your logout logic...
};

function TopBar({ title }) {
  const { activeAlarms, toggleBuzzer } = useContext(AlarmContext);

  const [showPanel, setShowPanel] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [showProductionSummary, setShowProductionSummary] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);

  const audioRefs = useRef({});

  const [pos, setPos] = useState({ x: 300, y: 100 });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const startDrag = (e) => {
    dragging.current = true;
    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y
    };
  };

  const onDrag = (e) => {
    if (!dragging.current) return;

    setPos({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y
    });
  };

  const stopDrag = () => {
    dragging.current = false;
  };

  // ================= ALARM SOUND (UNCHANGED) =================
  useEffect(() => {
    activeAlarms.forEach(alarm => {
      const existingAudio = audioRefs.current[alarm.tag];

      if (!existingAudio && alarm.buzzerOn) {
        const audio = new Audio("/sounds/Alarm.m4a");
        audio.loop = true;
        audio.play().catch(() => {});
        audioRefs.current[alarm.tag] = audio;
      }

      if (existingAudio && !alarm.buzzerOn) {
        existingAudio.pause();
      }

      if (existingAudio && alarm.buzzerOn) {
        existingAudio.play().catch(() => {});
      }
    });

    Object.keys(audioRefs.current).forEach(tag => {
      const stillExists = activeAlarms.find(a => a.tag === tag);

      if (!stillExists) {
        audioRefs.current[tag]?.pause();
        delete audioRefs.current[tag];
      }
    });

  }, [activeAlarms]);

  const hasAlarm = activeAlarms.length > 0;

  // ================= SAFE OUTSIDE CLICK (ONLY CLOSE HELP) =================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".submenu") && !e.target.closest(".menu-item")) {
        setShowHelpMenu(false);
      }
    };

    if (showHelpMenu) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showHelpMenu]);

  return (
    <>
      <div className="topbar">

        {/* TITLE */}
        <div className="topbar-title">{title}</div>

        {/* ALARMS (UNCHANGED) */}
        <div className="alarm-container">
          <div className="alarm-header">
            <span>DATE</span>
            <span>TAG</span>
            <span>DESCRIPTION</span>
            <span>ALARM TYPE</span>
            <span>VALUE</span>
          </div>

          <div className="alarm-scroll">
            {activeAlarms.length === 0 ? (
              <div className="no-alarm">No Active Alarm</div>
            ) : (
              activeAlarms.map((alarm, i) => (
                <div key={i} className="alarm-row">
                  <span>{alarm.timestamp}</span>
                  <span>{alarm.tag}</span>
                  <span>{alarm.description}</span>
                  <span>{alarm.type}</span>
                  <span>{alarm.value.toFixed(2)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ALARM BELL (UNCHANGED) */}
        <div
          className={`alarm-bell ${hasAlarm ? "blink" : ""}`}
          onClick={() => setShowPanel(true)}
        >
          🔔
        </div>

        {/* RIGHT MENU */}
        <div className="topbar-right">

          <div className="user-avatar">
              <img
                src={localStorage.getItem("userImage") || defaultAvatar}
                alt="user"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultAvatar;
                }}
              />
            </div>

          <div className="menu-wrapper">

            {/* FIXED MENU BUTTON (ONLY ONE CLICK HANDLER) */}
            <div
              className="menu-button"
              onClick={() => {
                setShowMenu(prev => !prev);
                setShowHelpMenu(false); // close help when menu toggles
              }}
            >
              Menu ▼
            </div>

            {/* MAIN MENU (UNCHANGED) */}
            {showMenu && (
              <div className="menu-dropdown">

                <div
                  className="menu-item"
                  onClick={() => setShowHelpMenu(prev => !prev)}
                >
                  Help ❓
                </div>

                <div
                  className="menu-item"
                  onClick={() => {
                    setShowProductionSummary(true);
                    setShowMenu(false);
                  }}
                >
                  Production Summary
                </div>

                <div className="menu-item" onClick={() => setShowPanel(true)}>
                  Alarm Status
                </div>

                <div
                  className="menu-item"
                  onClick={() => {
                    setShowUserInfo(true);
                    setShowMenu(false);
                  }}
                >
                  User Information
                </div>

                <div
                  className="menu-item logout-item"
                  onClick={() => window.location.href = "/"}
                >
                  Logout
                </div>

              </div>
            )}

            {/* HELP SUBMENU (UNCHANGED CONTENT) */}
            {showHelpMenu && (
              <div className="submenu">

                <div
                  className="submenu-item"
                  onClick={() => {
                    setShowFAQ(true);
                    setShowHelpMenu(false);
                    setShowMenu(false);
                  }}
                >
                  FAQ ❓
                </div>

                <div
                  className="submenu-item"
                  onClick={() => window.open("https://chat.whatsapp.com/FpyznaroihJ5bVCE9YnbSh", "_blank")}
                >
                  WhatsApp Group
                </div>

                <div
                  className="submenu-item"
                  onClick={() => window.open("https://t.me/+0NpYjr5Gs8ZiOGY0", "_blank")}
                >
                  Telegram Group
                </div>

                <div
                  className="submenu-item"
                  onClick={() => window.open("https://www.linkedin.com/company/nk-tech-hub/", "_blank")}
                >
                  Linkedlen
                </div>

                <div
                  className="submenu-item"
                  onClick={() => window.open("https://www.nktechhub.com/courses/read/47/offshore-production-dcs-simulation-and-operations-training", "_blank")}
                >
                  Buy Course
                </div>

              </div>
            )}

          </div>
        </div>
      </div>

      {/* ================= FLOATING PANEL ================= */}
      {showPanel && (
        <div className="alarm-modal" 
              onMouseMove={onDrag}
              onMouseUp={stopDrag}>
          <div className="alarm-modal-content" style={{left: pos.x, top: pos.y}}>

            {/* HEADER */}
            <div className="alarm-modal-header" 
                onMouseDown={startDrag}
                style={{ cursor: "move" }}>
              <h3 className="nk-title">NK Tech DCS Alarm List</h3>
              <button onClick={() => setShowPanel(false)}>X</button>
            </div>

            {/* TABLE HEADER */}
            <div className="alarm-header full">
              <span>DATE</span>
              <span>TAG</span>
              <span>DESCRIPTION</span>
              <span>TYPE</span>
              <span>VALUE</span>
              <span>BUZZER</span>
            </div>

            {/* TABLE BODY */}
            <div className="alarm-scroll full">
              {activeAlarms.length === 0 ? (
                <div className="no-alarm">No Active Alarm</div>
              ) : (
                activeAlarms.map((alarm, i) => (
                  <div key={i} className={`alarm-row full ${alarm.severity}`}>
                    <span>{alarm.timestamp}</span>
                    <span>{alarm.tag}</span>
                    <span>{alarm.description}</span>
                    <span>{alarm.type}</span>
                    <span>{alarm.value.toFixed(2)}</span>

                    {/* 🔘 BUZZER BUTTON */}
                    <button
                      className={alarm.buzzerOn ? "buzzer-on" : "buzzer-off"}
                      onClick={() => toggleBuzzer(alarm.tag)}
                    >
                      {alarm.buzzerOn ? "ON 🔊" : "OFF 🔇"}
                    </button>
                  </div>
                ))
              )}
            </div>

          </div>
        </div>
      )}

      <UserInformation open={showUserInfo} onClose={() => setShowUserInfo(false)} />

      <ProductionSummary
        open={showProductionSummary}
        onClose={() => setShowProductionSummary(false)}
      />

      <FAQ
        open={showFAQ}
        onClose={() => setShowFAQ(false)}
      />

    </>
  );
}

export default TopBar;